import React from "react";

import deepEqual from "deep-equal";
import { useToasts } from "react-toast-notifications";
import { BooleanParam, useQueryParam, withDefault } from "use-query-params";

import { NumberRangePicker } from "@components/number-range-picker";
import {
  ControlCenterWrapper,
  ControlWrapper,
} from "@components/reports/control-center-utilities";
import { NumberInput } from "@components/utilities";
import { useNorthbeamQuery } from "@hooks/http";
import { logEvent } from "@internal/analytics";
import {
  type CampaignSearch,
  type CampaignSearchVariables,
  type CustomerPathFilterStage,
  type PathDateRangeFilterType,
} from "@north-beam/nb-client-side";
import {
  type BreakdownConfig,
  type FixedDateInterval,
  DateRangeChoiceEnum,
  type DateRangeChoiceV2,
} from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { DateRangeDisplay, DateRangePicker } from "@shared/date-range-picker";
import { Select } from "@shared/selects";
import { Switch } from "@shared/switch";

import { PathElementFilterComponent } from "./path-element-filter";
import { CAMPAIGN_SEARCH } from "./queries";
import { PathDateRangeFilterType_, type ReportState } from "./report-state";

export interface ControlCenterProps {
  breakdownConfigs: BreakdownConfig[];
  onReportStateUpdated: (state: ReportState) => void;
  isLoading: boolean;
  initialState: ReportState;
}

export function ControlCenter(props: ControlCenterProps) {
  const { breakdownConfigs, initialState, isLoading, onReportStateUpdated } =
    props;

  const { addToast } = useToasts();
  const { refetch } = useNorthbeamQuery<
    CampaignSearch,
    CampaignSearchVariables
  >(CAMPAIGN_SEARCH, {
    variables: { query: "" },
    skip: true,
    fetchPolicy: "no-cache",
  });

  const fetchCampaigns = React.useCallback(
    async (query: string) => {
      const { data } = await refetch({ query });
      const campaigns = data?.me?.campaigns;
      if (!campaigns) {
        addToast(
          "Whoops! an error occurred while searcing. Please try again!",
          { appearance: "error" },
        );
      }
      return campaigns ?? [];
    },
    [refetch, addToast],
  );

  const [proposedState, setProposedState] = React.useState(initialState);

  React.useEffect(() => {
    setProposedState(initialState);
  }, [initialState]);

  const {
    pathLengthMaximum,
    pathLengthMinimum,
    dateRange,
    pathDateRangeFilterType,
    pathFilterStages,
    showConvertingPathsOnly,
    limit,
  } = proposedState;

  const dispatch = React.useCallback(
    (action: ReportAction) => {
      const newState = ReportStateReducer(proposedState, action);
      setProposedState(newState);
    },
    [proposedState],
  );

  // Path Length
  const pathLenthBounds = { minimum: 1, maximum: 100 };
  const pathLength = {
    minimum: pathLengthMinimum ?? pathLenthBounds.minimum,
    maximum: pathLengthMaximum ?? pathLenthBounds.maximum,
  };

  return (
    <ControlCenterWrapper>
      <ControlWrapper>
        <Select
          isLoading={isLoading}
          onChange={(value) => {
            logEvent("Customer paths: Show me paths dropdown", { value });
            dispatch({
              type: "UpdateShowConvertingPathsOnly",
              value: value === "true",
            });
          }}
          value={showConvertingPathsOnly ? "true" : "false"}
          icon={<i className="fa-regular fa-sharp fa-filter" />}
          options={[
            {
              value: "true",
              label: "Show me converting paths only",
            },
            {
              value: "false",
              label: "Show me all paths",
            },
          ]}
        />
      </ControlWrapper>
      <ControlWrapper>
        <Select
          isLoading={isLoading}
          onChange={(value) => {
            logEvent("Customer paths: time intersection dropdown", { value });
            dispatch({
              type: "UpdatePathDateRangeFilterType",
              value,
            });
          }}
          icon={<i className="fa-regular fa-sharp fa-filter" />}
          value={pathDateRangeFilterType}
          options={PathDateRangeFilterType_.reactSelectOptions}
        />
      </ControlWrapper>
      <ControlWrapper>
        <DateRangePicker
          label="DATE RANGE"
          popoverPlacement="bottom-start"
          disabled={isLoading}
          nonCustomChoices={DateRangeChoiceEnum.reactSelectOptions}
          dateRange={dateRange}
          onUpdate={(value) => {
            logEvent("Customer paths: date range selector", { value });
            dispatch({
              type: "UpdateDateRange",
              value,
            });
          }}
        />
        <small className="text-muted mt-1 text-small">
          <DateRangeDisplay
            dateRange={dateRange}
            onConversionToFixed={(value) =>
              dispatch({
                type: "UpdateDateRange",
                value,
              })
            }
          />
        </small>
      </ControlWrapper>
      <ControlWrapper>
        <hr />
      </ControlWrapper>
      <ControlWrapper title="PATH LENGTH">
        <NumberRangePicker
          disabled={isLoading}
          bounds={{ minimum: 1, maximum: 100 }}
          value={pathLength}
          noun="element"
          onUpdate={(range) =>
            dispatch({
              type: "UpdatePathLength",
              value: range,
            })
          }
        />
      </ControlWrapper>
      <div className="my-2">
        <ControlWrapper title="PATH FILTER">
          <PathElementFilterComponent
            disabled={isLoading}
            maxStages={4}
            fetchCampaigns={fetchCampaigns}
            current={pathFilterStages}
            breakdownConfigs={breakdownConfigs}
            onUpdate={(value) =>
              dispatch({
                type: "UpdatePathFilterStages",
                value,
              })
            }
          />
        </ControlWrapper>
      </div>
      <ControlWrapper title="NUMBER OF PATHS TO SHOW">
        <NumberInput
          value={limit}
          format="integer"
          onValueChange={(value) => {
            if (typeof value === "number") {
              dispatch({
                type: "UpdateLimit",
                value: value,
              });
            }
          }}
        />
      </ControlWrapper>
      <ControlWrapper>
        <PrimaryButton
          size="md"
          disabled={isLoading || deepEqual(initialState, proposedState)}
          onClick={() => onReportStateUpdated(proposedState)}
        >
          UPDATE REPORT
        </PrimaryButton>
      </ControlWrapper>
    </ControlCenterWrapper>
  );
}

interface UpdateDateRange {
  type: "UpdateDateRange";
  value: DateRangeChoiceV2 | FixedDateInterval;
}

interface UpdateShowConvertingPathsOnly {
  type: "UpdateShowConvertingPathsOnly";
  value: boolean;
}

interface UpdatePathDateRangeFilterType {
  type: "UpdatePathDateRangeFilterType";
  value: PathDateRangeFilterType;
}

interface UpdatePathLength {
  type: "UpdatePathLength";
  value: { minimum: number; maximum: number };
}

interface UpdatePathFilterStages {
  type: "UpdatePathFilterStages";
  value: CustomerPathFilterStage[];
}

interface UpdateLimit {
  type: "UpdateLimit";
  value: number;
}

interface SetReport {
  type: "SetReport";
  value: ReportState | ((s: ReportState) => ReportState);
}

export type ReportAction =
  | UpdateDateRange
  | UpdateShowConvertingPathsOnly
  | UpdatePathDateRangeFilterType
  | UpdatePathLength
  | UpdatePathFilterStages
  | UpdateLimit
  | SetReport;

export function ReportStateReducer(
  state: ReportState,
  action: ReportAction,
): ReportState {
  switch (action.type) {
    case "UpdateDateRange":
      return { ...state, dateRange: action.value };
    case "UpdateShowConvertingPathsOnly":
      return { ...state, showConvertingPathsOnly: action.value };
    case "UpdatePathDateRangeFilterType":
      return { ...state, pathDateRangeFilterType: action.value };
    case "UpdatePathLength":
      return {
        ...state,
        pathLengthMinimum: action.value.minimum,
        pathLengthMaximum: action.value.maximum,
      };
    case "UpdatePathFilterStages":
      return { ...state, pathFilterStages: action.value };
    case "UpdateLimit":
      return { ...state, limit: action.value };

    case "SetReport":
      if (typeof action.value === "function") {
        return action.value(state);
      }

      return action.value;
  }
}
